import { ImageLabelType } from '@models/ImageFavorite/imageFavorite.enums';
import { ImageLabelProperties } from '@models/ImageFavorite/imageLabelProperties.model';
import { ImageLabelValue } from '@models/ImageFavorite/imageLabelValue.model';
import { EditableLabel } from '@models/Labels/editableLabel.model';

// Label look-and-feel shared by image, map and chart favorites, so a label
// styled in one looks the same in the others.

// Label font sizes are authored for a 600px-wide surface (a Medium tile) and scale with the real width.
export const LABEL_BASE_WIDTH_PX = 600;

export function labelFontScale(widthPx: number): number {
  return Math.min(2, Math.max(0.5, widthPx / LABEL_BASE_WIDTH_PX));
}

export function labelTypeHasArrow(labelType: number): boolean {
  return labelType === ImageLabelType.TextArrow || labelType === ImageLabelType.StickyArrow;
}


// Parse a label's JSON properties bag and fill in the defaults for its type, so
// older labels (and ones with a missing/invalid bag) render sensibly.
export function parseLabelProps(label: EditableLabel): ImageLabelProperties {
  let p: ImageLabelProperties = {};
  const raw = label.properties ?? '';
  if (raw !== '') {
    try {
      p = JSON.parse(raw);
    } catch {
      p = {};
    }
  }
  return {
    fontSize: p.fontSize ?? 12,
    fontColor: p.fontColor ?? '#222222',
    hasStickyBackground: p.hasStickyBackground ?? (label.labelType === ImageLabelType.StickyArrow),
    hasShadow: p.hasShadow ?? (label.labelType === ImageLabelType.StickyArrow || label.labelType === ImageLabelType.Box),
    showArrow: p.showArrow ?? labelTypeHasArrow(label.labelType),
    regionColor: p.regionColor ?? '#ffd966',
    decimals: p.decimals ?? 1,
    units: p.units ?? '',
    valueFontSize: p.valueFontSize ?? 18
  };
}


// Properties for a brand-new label of the given type.
export function newLabelProps(labelType: number): ImageLabelProperties {
  return {
    fontSize: 12,
    fontColor: '#222222',
    hasStickyBackground: labelType === ImageLabelType.StickyArrow,
    hasShadow: labelType === ImageLabelType.StickyArrow || labelType === ImageLabelType.Box,
    showArrow: labelTypeHasArrow(labelType),
    regionColor: '#ffd966'
  };
}


// Box styling, minus position and size (which the host computes: percent of the
// image for image labels, projected pixels for map labels). `fontScale` is 1 unless
// the host scales fonts with its size.
export function labelBoxStyle(label: EditableLabel, props: ImageLabelProperties, fontScale = 1): { [key: string]: any } {
  const style: { [key: string]: any } = {
    'font-size.px': props.fontSize * fontScale,
    'color': props.fontColor
  };

  if (label.labelType === ImageLabelType.Region) {
    style['background-color'] = withAlpha(props.regionColor, 0.3);
    style['border-color'] = props.regionColor;
    return style;
  }

  if (props.hasStickyBackground) {
    style['background-color'] = '#ffe17a';
    style['border-color'] = '#d6b656';
  } else if (label.labelType === ImageLabelType.Box || label.isDynamic) {
    style['background-color'] = 'rgba(255, 255, 255, 0.92)';
    style['border-color'] = '#bfbfbf';
  }
  if (props.hasShadow) {
    style['box-shadow'] = '2px 3px 6px rgba(0, 0, 0, 0.45)';
  }
  return style;
}


export function labelValueStyle(props: ImageLabelProperties, fontScale = 1): { [key: string]: any } {
  return { 'font-size.px': props.valueFontSize * fontScale };
}


export function arrowColor(props: ImageLabelProperties): string {
  return props.hasStickyBackground ? '#d6b656' : props.fontColor;
}


export function formatLabelValue(props: ImageLabelProperties, value: ImageLabelValue | undefined): string {
  if (value == undefined || value.value == undefined) {
    return '--';
  }
  const decimals = Math.min(6, Math.max(0, Number(props.decimals ?? 1)));
  const units = (props.units ?? '').trim();
  return value.value.toFixed(decimals) + (units !== '' ? ' ' + units : '');
}


export function labelValueTooltip(value: ImageLabelValue | undefined): string {
  if (value == undefined || value.date == undefined) {
    return 'No recent readings';
  }
  return `${value.readingCount} reading(s), latest ${new Date(value.date).toLocaleString()}`;
}


export function withAlpha(hex: string, alpha: number): string {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex ?? '');
  if (m == null) {
    return hex;
  }
  return `rgba(${parseInt(m[1], 16)}, ${parseInt(m[2], 16)}, ${parseInt(m[3], 16)}, ${alpha})`;
}
